import type { AuditLog } from '@/types';

import { normalizeAuditResource } from './auditLogFilters';

export const AUDIT_LOG_NESTING_WINDOW_MS = 30_000;

export interface NestedAuditLog {
    depth: 0 | 1;
    log: AuditLog;
}

interface AuditLogParentReference {
    parentId: string;
    parentType: string;
}

interface AuditLogRelationship {
    childType: string;
    detailKey: string;
    parentType: string;
}

const auditLogRelationships: AuditLogRelationship[] = [
    { childType: 'Report Component', detailKey: 'report', parentType: 'Report' },
    { childType: 'List View', detailKey: 'list', parentType: 'List' },
    { childType: 'Memo Collaborator', detailKey: 'memo', parentType: 'Memo' },
];

const recordValue = (value: unknown): Record<string, unknown> | undefined =>
    value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;

const stringValue = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : undefined);

const getParentReference = (log: AuditLog): AuditLogParentReference | undefined => {
    const resourceType = normalizeAuditResource(log.entityType);
    const relationship = auditLogRelationships.find(({ childType }) => childType === resourceType);

    if (!relationship) {
        return undefined;
    }

    const details = recordValue(log.details);
    const nestedParentId = recordValue(details?.[relationship.detailKey])?.id;
    const parentId = stringValue(nestedParentId) ?? stringValue(details?.[`${relationship.detailKey}Id`]);

    return parentId ? { parentId, parentType: relationship.parentType } : undefined;
};

const isParentLog = (log: AuditLog, reference: AuditLogParentReference) =>
    normalizeAuditResource(log.entityType) === reference.parentType && log.entityId === reference.parentId;

const actorId = (log: AuditLog) => log.member?.id ?? log.user?.id;

const occurredAt = (log: AuditLog) => new Date(log.createdAt).getTime();

const isImmediateSequence = (parent: AuditLog, child: AuditLog) => {
    const parentActorId = actorId(parent);
    const childActorId = actorId(child);
    const elapsed = Math.abs(occurredAt(parent) - occurredAt(child));

    return (
        Boolean(parentActorId) &&
        parentActorId === childActorId &&
        Number.isFinite(elapsed) &&
        elapsed <= AUDIT_LOG_NESTING_WINDOW_MS
    );
};

const isChildOf = (log: AuditLog, parent: AuditLog) => {
    const reference = getParentReference(log);

    return Boolean(reference && isParentLog(parent, reference) && isImmediateSequence(parent, log));
};

/**
 * Visually nests only contiguous, actor-matched parent/child mutations. The
 * parent can arrive on either side of its children because the audit query is
 * newest-first while related writes may finish concurrently.
 */
export const nestImmediateAuditLogs = (logs: AuditLog[]): NestedAuditLog[] => {
    const nestedLogs: NestedAuditLog[] = [];

    for (let index = 0; index < logs.length;) {
        const log = logs[index];
        const followingLog = logs[index + 1];

        if (followingLog && isChildOf(followingLog, log)) {
            nestedLogs.push({ depth: 0, log });
            index += 1;

            while (index < logs.length && isChildOf(logs[index], log)) {
                nestedLogs.push({ depth: 1, log: logs[index] });
                index += 1;
            }

            continue;
        }

        const parentReference = getParentReference(log);

        if (parentReference) {
            const children: AuditLog[] = [];
            let candidateIndex = index;

            while (candidateIndex < logs.length) {
                const candidateReference = getParentReference(logs[candidateIndex]);

                if (
                    !candidateReference ||
                    candidateReference.parentId !== parentReference.parentId ||
                    candidateReference.parentType !== parentReference.parentType
                ) {
                    break;
                }

                children.push(logs[candidateIndex]);
                candidateIndex += 1;
            }

            const parent = logs[candidateIndex];

            if (
                parent &&
                isParentLog(parent, parentReference) &&
                children.every((child) => isImmediateSequence(parent, child))
            ) {
                nestedLogs.push({ depth: 0, log: parent });
                nestedLogs.push(...children.map((child): NestedAuditLog => ({ depth: 1, log: child })));
                index = candidateIndex + 1;
                continue;
            }
        }

        nestedLogs.push({ depth: 0, log });
        index += 1;
    }

    return nestedLogs;
};
